<template>
    <li class="flex flex-col gap-0.5">
        <div
            class="flex flex-row gap-2 justify-between items-center pl-7 before:absolute before:left-2.75 before:mt-[calc(0.5lh-0.25rem)] before:text-[0.5rem]/none before:content-['■'] relative pr-2 border-l-3 pt-0.5"
            :class="[
                {
                    'pb-0.5': !hasSlotContent,
                    'border-l-transparent': !props.isMainVariant,
                    'bg-linear-to-r to-transparent to-80%': props.isMainVariant,
                },
                colorClasses,
            ]"
        >
            <div class="flex flex-row items-center gap-2">
                <h3 class="flex flex-row items-center gap-1 text-xl">
                    <AllocationVariantIndex :index="props.allocationVariantIndex" text-size="Inherit" />
                    <span v-if="props.title" class="font-light text-gray-400">({{ props.title }})</span>
                </h3>

                <LinkButton
                    class="text-gray-700 self-center bg-gray-50 rounded-md"
                    size="Small"
                    variant="Transparent-Light"
                    :icon-description="$t('open-allocation-variant-detail-page')"
                    :open-in-new-tab="true"
                    :to="props.linkTarget"
                >
                    <template #icon>
                        <ExternalLinkIcon class="size-4.5" />
                    </template>
                </LinkButton>

                <Badge size="Small" :variant="props.statusColor">
                    {{ props.statusLabel }}
                </Badge>

                <MainVariantIndicator v-if="props.isMainVariant" size="Small" :color="categoryColor" />
            </div>

            <div>
                <ActivatedToggle
                    v-if="props.visibility === 'Visible' || props.visibility === 'Invisible'"
                    :is-active="props.visibility === 'Visible'"
                    :tooltip-text="
                        props.visibility === 'Visible'
                            ? $t('click-to-hide-allocation-variant-in-timeline')
                            : $t('click-to-show-allocation-variant-in-timeline')
                    "
                    size="Small"
                    @toggle="
                        triggerHideTooltip();
                        emit('toggle-visibility', getToggleValue());
                    "
                >
                    {{ $t('allocation-variant-visible') }}
                </ActivatedToggle>

                <div
                    v-else
                    class="flex flex-row items-center font-light gap-1 text-yellow-800 bg-yellow-100 pl-2 pr-2.5 rounded-full leading-none py-1.5 min-w-max ml-2"
                >
                    <template v-if="props.visibility === 'Outside-Timeline-Range'">
                        <WarningIcon class="size-5 text-yellow-600" />{{ $t('outside-timeline-range') }}
                    </template>
                    <template v-if="props.visibility === 'No-Allocations'">
                        <WarningIcon class="size-5 text-yellow-600" />{{ $t('no-allocations') }}
                    </template>
                </div>
            </div>
        </div>

        <slot />
    </li>
</template>

<script setup lang="ts">
import { VariantStateVisibility } from '@/allocation-designer/AllocationSelectionFilter.state.ts';
import { computed, inject, provide, ref, useSlots } from 'vue';
import { RouteLocationRaw } from 'vue-router';
import ActivatedToggle from '../ActivatedToggle/ActivatedToggle.vue';
import AllocationVariantIndex from '../AllocationVariantIndex/AllocationVariantIndex.vue';
import Badge, { BadgeVariant } from '../Badge/Badge.vue';
import LinkButton from '../Button/LinkButton.vue';
import ExternalLinkIcon from '../Icon/ExternalLinkIcon.vue';
import WarningIcon from '../Icon/WarningIcon.vue';
import MainVariantIndicator from '../MainVariantIndicator/MainVariantIndicator.vue';
import { PhaseColor } from '../Timeline/Timeline.types.ts';
import { triggerHideTooltipKey } from '../Tooltip/injectionKeys';
import { allocationGroupKey } from './injectionKeys';

export type AllocationVariantVisibility = 'Visible' | 'Invisible' | 'Outside-Timeline-Range' | 'No-Allocations';

type AllocationVariantListEntryProps = {
    allocationVariantIndex: number;
    title: string;
    linkTarget: RouteLocationRaw;
    statusColor: BadgeVariant;
    statusLabel: string;
    visibility: AllocationVariantVisibility;
    isMainVariant: boolean;
    categoryColor: Extract<
        PhaseColor,
        | 'External-Event-Category-Project'
        | 'External-Event-Category-Event'
        | 'External-Event-Category-External-Event'
        | 'Project'
        | 'Event'
    >;
};

type AllocationVariantListEntryEmits = {
    (e: 'toggle-visibility', v: VariantStateVisibility): void;
};

const props = defineProps<AllocationVariantListEntryProps>();
const emit = defineEmits<AllocationVariantListEntryEmits>();

function getToggleValue() {
    if (props.visibility === 'Visible') {
        return 'Invisible';
    } else {
        return 'Visible';
    }
}

const injectedValues = inject(allocationGroupKey);
if (!injectedValues) {
    throw new Error('AllocationVariantListEntry must be used within a AllocationSelectionPanel component');
}

const slots = useSlots();
const hasSlotContent = computed(() => slots.default?.().some((vnode) => typeof vnode.type !== 'symbol') ?? false);

// This is used to force close the Tooltip after clicking the button
const triggerHideTooltipKeyCounter = ref(0);
provide(triggerHideTooltipKey, triggerHideTooltipKeyCounter);

function triggerHideTooltip() {
    triggerHideTooltipKeyCounter.value = triggerHideTooltipKeyCounter.value + 1;
}

const colorClasses = computed(() => {
    if (props.isMainVariant) {
        switch (props.categoryColor) {
            case 'Event':
            case 'External-Event-Category-Event':
                return 'from-sky-100 border-l-sky-700';
            case 'Project':
            case 'External-Event-Category-Project':
                return 'from-rose-100 border-l-rose-700';
            case 'External-Event-Category-External-Event':
                return 'from-green-100 border-l-green-700';
        }
    }
});
</script>
